import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { levelLabel } from "@/utils/levelLabels";
import type { Question } from "@shared/schema";

const LEVEL_OPTIONS: { value: Question["level"]; label: string }[] = [
  { value: "Knowledge", label: "Knowledge" },
  { value: "Understanding", label: "Understanding" },
  { value: "Higher-Order Thinking", label: "Application+" },
];

export type BlueprintDecision = "update" | "keep";

interface RegenerateQuestionDialogProps {
  question: Question;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Whether the parent can amend the blueprint (creation flow). When false,
   *  a level change regenerates without the Update/Keep prompt. */
  canAmendBlueprint?: boolean;
  onConfirm: (targetLevel: Question["level"], blueprintDecision?: BlueprintDecision) => void;
}

/** Whole-item regenerate dialog: pick the target cognitive level (prefilled
 *  from the item's current level), confirm, and when the level changed decide
 *  whether the approved blueprint should follow. The item is only regenerated
 *  (and the blueprint only amended) after an explicit confirm. */
export function RegenerateQuestionDialog({
  question,
  open,
  onOpenChange,
  canAmendBlueprint = true,
  onConfirm,
}: RegenerateQuestionDialogProps) {
  const [targetLevel, setTargetLevel] = useState<Question["level"]>(question.level);
  const [askingBlueprint, setAskingBlueprint] = useState(false);

  // Reset to the item's current level every time the dialog opens so a stale
  // previous selection can never leak into a new regeneration.
  useEffect(() => {
    if (open) {
      setTargetLevel(question.level);
      setAskingBlueprint(false);
    }
  }, [open, question.level]);

  const handleConfirm = () => {
    if (targetLevel === question.level || !canAmendBlueprint) {
      onConfirm(targetLevel);
      onOpenChange(false);
    } else {
      setAskingBlueprint(true);
    }
  };

  const handleDecision = (decision: BlueprintDecision) => {
    onConfirm(targetLevel, decision);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid={`dialog-regenerate-${question.id}`}>
        {!askingBlueprint ? (
          <>
            <DialogHeader>
              <DialogTitle>Regenerate this item?</DialogTitle>
              <DialogDescription>
                Choose the cognitive level for the new item. It is prefilled
                with the current level of this item.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-2">
              <label className="text-sm font-medium">Cognitive level</label>
              <Select value={targetLevel} onValueChange={(v) => setTargetLevel(v as Question["level"])}>
                <SelectTrigger
                  className="w-full"
                  data-testid={`select-regenerate-level-${question.id}`}
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {LEVEL_OPTIONS.map((l) => (
                    <SelectItem key={l.value} value={l.value}>
                      {l.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleConfirm}
                data-testid={`button-confirm-regenerate-${question.id}`}
              >
                Regenerate item
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Update your blueprint to match?</DialogTitle>
              <DialogDescription>
                You've changed this item from {levelLabel(question.level)} to{" "}
                {levelLabel(targetLevel)}. Update the requested blueprint so the
                conformance check stops reporting a deviation, or keep the
                blueprint as approved.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => handleDecision("keep")}
                data-testid={`button-blueprint-update-no-${question.id}`}
              >
                Keep blueprint
              </Button>
              <Button
                onClick={() => handleDecision("update")}
                data-testid={`button-blueprint-update-yes-${question.id}`}
              >
                Update blueprint
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}